/**
 * Socket-level remote kitchen print (Electron).
 * Runs from socketService so printing works on any screen, not only when React listeners mount.
 */
import {
  handleRemoteOrderCreated,
  handleRemoteOrderUpdated,
  getRemotePrintSkipReason,
  snapshotOrderItems,
  isPrintStationEnabled,
  enqueueRemoteKitchenPrint,
  shouldSkipRemoteSocketPrint,
  tryAcquireRemoteCreatePrint,
  releaseRemoteCreatePrint,
} from './remotePrintStation';
import { isAutoPrintModificationEnabled, sendToCounterPrinter } from './printService';

const isElectronApp = () =>
  typeof window !== 'undefined' && !!window.electronAPI?.isElectron;

const previousOrders = new Map();
const SNAPSHOT_STORAGE_PREFIX = 'pos_remote_print_snap_';
let ctxBranchId = null;

let ctx = {
  branchId: null,
  remotePrintUserId: null,
  currentUser: null,
  branchMeta: null,
  tables: [],
  t: (key) => key,
  language: null,
};

export function persistRemoteOrderSnapshot(orderId, orderOrItems) {
  if (!orderId) return;
  const items = Array.isArray(orderOrItems)
    ? orderOrItems
    : snapshotOrderItems(orderOrItems);
  previousOrders.set(orderId, { items });
  try {
    sessionStorage.setItem(
      `${SNAPSHOT_STORAGE_PREFIX}${orderId}`,
      JSON.stringify(items)
    );
  } catch (_) {
    /* ignore quota */
  }
}

function loadOrderSnapshot(orderId) {
  if (!orderId) return null;
  const mem = previousOrders.get(orderId);
  if (mem?.items?.length) return mem;

  try {
    const raw = sessionStorage.getItem(`${SNAPSHOT_STORAGE_PREFIX}${orderId}`);
    if (!raw) return null;
    const items = JSON.parse(raw);
    if (Array.isArray(items) && items.length > 0) {
      const snap = { items };
      previousOrders.set(orderId, snap);
      return snap;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

export function setRemotePrintSocketContext(partial) {
  const nextBranch =
    partial.branchId != null ? String(partial.branchId) : null;

  if (
    nextBranch != null &&
    ctxBranchId != null &&
    nextBranch !== ctxBranchId
  ) {
    previousOrders.clear();
  }
  if (nextBranch != null) {
    ctxBranchId = nextBranch;
  }

  ctx = { ...ctx, ...partial };
}

export function clearRemotePrintSocketContext() {
  ctxBranchId = null;
  ctx = {
    branchId: null,
    remotePrintUserId: null,
    currentUser: null,
    branchMeta: null,
    tables: [],
    t: (key) => key,
    language: null,
  };
  previousOrders.clear();
}

function logSkip(reason, extra = {}) {
  console.warn('[print-station] Remote kitchen print skipped:', reason, extra);
}

function orderBranchId(order, data) {
  return order?.branchId || order?.branch_id || data?.branchId || null;
}

/**
 * Sync replays must never auto-print: when a device comes back online, the
 * server re-broadcasts orders/modifications that were made (and printed
 * locally) while offline.
 *
 * This is detected by the explicit `syncReplay` flag set by the backend sync
 * controller — a deterministic signal. (An earlier timestamp-based heuristic
 * was removed: it skipped legitimate tickets for tables that had been open a
 * while, or whenever the POS PC's clock was off.) The existing per-order-id
 * dedup (tryAcquireRemoteCreatePrint / hasRemoteCreateAlreadyPrinted) still
 * guards against duplicate broadcasts of the same id.
 */
function isSyncReplayEvent(data /* , order, kind */) {
  return data?.syncReplay === true;
}

function matchesBranch(order, data) {
  const bid = orderBranchId(order, data);
  if (!bid || !ctx.branchId) return true;
  return String(bid) === String(ctx.branchId);
}

async function refreshSnapshotAfterCreate(orderId) {
  if (!orderId) return;
  try {
    const { default: orderService } = await import('./orderService');
    let full = null;
    try {
      full = await orderService.getOrderById(orderId);
    } catch (_) {
      if (ctx.branchId) {
        const active = await orderService.getActiveOrders(ctx.branchId);
        full = (active || []).find(
          (o) => String(o._id || o.id) === String(orderId)
        );
      }
    }
    if (full) {
      persistRemoteOrderSnapshot(orderId, full);
    }
  } catch (_) {
    /* keep socket snapshot */
  }
}

export async function onSocketOrderCreated(data) {
  if (!isElectronApp() || !isPrintStationEnabled()) return;

  const order = data?.order ?? data;
  if (!order || typeof order !== 'object') return;
  if (!matchesBranch(order, data)) return;

  const skip = getRemotePrintSkipReason(ctx.remotePrintUserId, ctx.currentUser);
  if (skip) {
    logSkip(skip, {
      orderId: order._id || order.id,
      branchId: ctx.branchId,
      remotePrintUserId: ctx.remotePrintUserId,
      userId: ctx.currentUser?._id || ctx.currentUser?.id,
    });
    return;
  }

  const orderId = String(order._id || order.id || data?.orderId || '');

  if (isSyncReplayEvent(data, order, 'create')) {
    logSkip('sync replay (offline order already printed locally)', { orderId });
    if (orderId) persistRemoteOrderSnapshot(orderId, order);
    return;
  }

  if (shouldSkipRemoteSocketPrint(order, ctx.currentUser, 'create')) {
    if (orderId) persistRemoteOrderSnapshot(orderId, order);
    return;
  }

  if (orderId) {
    if (!tryAcquireRemoteCreatePrint(orderId)) {
      console.warn('[print-station] Skipped duplicate order_created:', orderId);
      persistRemoteOrderSnapshot(orderId, order);
      return;
    }
    persistRemoteOrderSnapshot(orderId, order);
  }

  const timezone = ctx.branchMeta?.timezone || null;

  enqueueRemoteKitchenPrint(async () => {
    const itemCount = (order?.items || order?.orderItems || []).length;
    console.info(
      '[print-station] Printing remote order_created',
      orderId || '(no id)',
      `(${itemCount} items in socket payload)`
    );
    try {
      await handleRemoteOrderCreated({
        order,
        tables: ctx.tables,
        t: ctx.t,
        language: ctx.language,
        timezone,
        remotePrintUserId: ctx.remotePrintUserId,
        currentUser: ctx.currentUser,
        branchId: ctx.branchId,
        enableCounterItems: ctx.branchMeta?.enableCounterItems ?? true,
      });
      await refreshSnapshotAfterCreate(orderId);
    } finally {
      if (orderId) releaseRemoteCreatePrint(orderId);
    }
  });
}

export async function onSocketOrderUpdated(data) {
  if (!isElectronApp() || !isPrintStationEnabled()) return;

  const order = data?.order ?? data?.orderData ?? data;
  if (!order || typeof order !== 'object') return;
  if (!matchesBranch(order, data)) return;

  const skip = getRemotePrintSkipReason(ctx.remotePrintUserId, ctx.currentUser);
  if (skip) return;

  const orderId = String(order._id || order.id || data?.orderId || '');

  if (isSyncReplayEvent(data, order, 'modify')) {
    logSkip('sync replay (offline modification already printed locally)', { orderId });
    if (orderId) persistRemoteOrderSnapshot(orderId, order);
    return;
  }

  if (shouldSkipRemoteSocketPrint(order, ctx.currentUser, 'modify')) {
    if (orderId) persistRemoteOrderSnapshot(orderId, order);
    return;
  }

  let previousSnapshot = orderId ? loadOrderSnapshot(orderId) : null;

  if (!previousSnapshot && orderId) {
    console.warn(
      '[print-station] order_updated: no prior snapshot in memory/storage, skipping modification ticket:',
      orderId
    );
    return;
  }

  const timezone = ctx.branchMeta?.timezone || null;

  enqueueRemoteKitchenPrint(async () => {
    console.info('[print-station] Printing remote order_updated', orderId);
    await handleRemoteOrderUpdated({
      order,
      previousOrder: { items: previousSnapshot.items },
      tables: ctx.tables,
      t: ctx.t,
      language: ctx.language,
      timezone,
      autoPrintModification: isAutoPrintModificationEnabled(),
      remotePrintUserId: ctx.remotePrintUserId,
      currentUser: ctx.currentUser,
      branchId: ctx.branchId,
    });
    if (orderId) {
      persistRemoteOrderSnapshot(orderId, order);
    }
  });
}

/**
 * Handles an incoming print_report socket event on the remote/counter print station.
 * Only runs on Electron devices with print station enabled.
 */
export async function onSocketPrintReport(data) {
  if (!isElectronApp() || !isPrintStationEnabled()) return;

  const reportText = data?.reportText;
  if (!reportText) {
    console.warn('[print-station] print_report received but no reportText in payload');
    return;
  }

  try {
    // logoUrl is present for remote bill receipts (from a printer-less device);
    // null for sales reports.
    await sendToCounterPrinter(reportText, data?.logoUrl || null);
    console.info('[print-station] Counter text printed via remote socket');
  } catch (err) {
    console.error('[print-station] Remote report print failed:', err);
  }
}
